import type { TreeMarkShape } from '@lib/enums/tree-mark.enum'
import { UNIDENTIFIED_MARK } from '@lib/enums/tree-mark.enum'
import type { TreeSpeciesMark } from '@lib/dto/tree-species.dto'
import { TREE_MARK_COLOR_STYLES } from '@/field-map/markerStyles'
import { cn } from '@/lib/utils'

/**
 * Outlines for the mark shapes, drawn in a 24×24 box.
 *
 * The first seven are the shapes the printed maps already use, so a pin on a
 * phone and a pin on the PDF a client received are recognisably the same mark.
 * The last two extend the set for species added since, and are drawn to the
 * same optical weight so one does not read as louder than the others.
 */
const SHAPE_PATHS: Record<TreeMarkShape, string> = {
  SQUARE: 'M5.5 5.5h13v13h-13z',
  TRIANGLE: 'M12 4.2 20 18.9H4z',
  CIRCLE: 'M12 4.3a7.7 7.7 0 1 1 0 15.4 7.7 7.7 0 0 1 0-15.4z',
  STAR: 'M12 3.4l2.36 5.06 5.54.68-4.09 3.77 1.08 5.48L12 15.66l-4.89 2.73 1.08-5.48-4.09-3.77 5.54-.68z',
  HEART:
    'M12 19.9c-1.2-.9-7.4-4.7-7.4-9.1 0-2.5 1.8-4.4 4-4.4 1.5 0 2.7.8 3.4 2 .7-1.2 1.9-2 3.4-2 2.2 0 4 1.9 4 4.4 0 4.4-6.2 8.2-7.4 9.1z',
  CROSS:
    'M6.6 4.1 12 9.5l5.4-5.4 2.5 2.5-5.4 5.4 5.4 5.4-2.5 2.5-5.4-5.4-5.4 5.4-2.5-2.5 5.4-5.4-5.4-5.4z',
  DIAMOND: 'M12 3.6 20.4 12 12 20.4 3.6 12z',
  HEXAGON: 'M12 3.7 19.2 7.85v8.3L12 20.3 4.8 16.15v-8.3z',
  PENTAGON: 'M12 3.7 20.2 9.65 17.07 19.3H6.93L3.8 9.65z',
}

export interface TreeGlyphProps {
  /**
   * The species being drawn, or null for a tree nobody has identified.
   *
   * Takes the mark by VALUE rather than a species id, so the same component
   * draws a live tree, a legend row, and a frozen proposal whose species may
   * since have been renamed or archived.
   */
  species: TreeSpeciesMark | null
  /** Rendered size in px. 18 on a map pin, 12–14 in a legend or list row. */
  size?: number
  className?: string
}

/**
 * The species mark itself.
 *
 * The white stroke is not decoration: the glyph sits on aerial photography that
 * ranges from bright pavement to near-black canopy shadow within a few pixels,
 * and a halo is what keeps a mid-tone hue readable against both. It is the same
 * reason cartographic labels have always been haloed.
 */
export function TreeGlyph({ species, size = 18, className }: TreeGlyphProps) {
  const mark = species ?? UNIDENTIFIED_MARK
  const { fillClass } = TREE_MARK_COLOR_STYLES[mark.color]

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={cn('shrink-0', className)}
      aria-hidden="true"
      focusable="false"
    >
      <path
        d={SHAPE_PATHS[mark.shape]}
        className={cn(fillClass, 'stroke-map-halo')}
        strokeWidth={2.25}
        strokeLinejoin="round"
        paintOrder="stroke fill"
      />
    </svg>
  )
}
